import { useEffect, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import { useGetUserProfile } from '@/api/user-dashboard/queries';
import PasskeyIcon from '@/assets/svg/ui-elements/passkey.svg';
import {
  dismissPasskeyOffer,
  hasPasskeyOffer,
  isPasskeyOfferDue,
  markPasskeyOnThisBrowser,
  takePasskeyOffer,
} from '@/features/auth/passkey-offer';
import { hasPlatformAuthenticator } from '@/features/auth/passkey-support';
import { MfaVerificationDialog } from '@/features/mfa/mfa-verification-dialog';
import { useMfaVerification } from '@/features/mfa/use-mfa-verification';
import { usePasskeysManagement } from '@/features/mfa/use-passkeys-management';
import { FeatureFlags, useFeatureFlag } from '@/global-hooks/use-feature-flag-enabled';
import { useIsMobile } from '@/global-hooks/use-is-mobile';
import { PagePaths } from '@/routes/page-paths';
import { BottomSheet, Modal, Typography, useToast } from '@/shared/design-system';

import { OfferDescription, OfferError } from './passkey-offer-dialog.styled';

const TITLE = 'Log in faster next time';
const DESCRIPTION =
  'Create a passkey to log in with Face ID, Touch ID or Windows Hello instead of typing a password.';
const AUTH_PATHS = [PagePaths.root, PagePaths.signUp, PagePaths.forgotPassword];

export const PasskeyOfferDialog = () => {
  const { user } = useGetUserProfile();
  const { pathname } = useLocation();
  const passkeysEnabled = useFeatureFlag(FeatureFlags.PASSKEYS_ENABLED);
  const isMobile = useIsMobile();
  const { add: addToast } = useToast();
  const [open, setOpen] = useState(false);
  const verification = useMfaVerification();
  const passkeys = usePasskeysManagement({ enabled: open, verification });
  const userId = user?.id;

  useEffect(() => {
    if (!userId || passkeysEnabled === undefined) return;
    if (!hasPasskeyOffer() || AUTH_PATHS.some((path) => matchPath(path, pathname))) return;
    let active = true;
    // The offer is taken only after the async check: the session gate remounts the app right after log in.
    void hasPlatformAuthenticator().then((available) => {
      if (!active || !takePasskeyOffer()) return;
      if (passkeysEnabled && available && isPasskeyOfferDue(userId)) setOpen(true);
    });
    return () => {
      active = false;
    };
  }, [passkeysEnabled, pathname, userId]);

  if (!userId) return null;

  const dismiss = () => {
    passkeys.cancelAdd();
    dismissPasskeyOffer(userId);
    setOpen(false);
  };

  const create = async () => {
    const outcome = await passkeys.add();
    if (outcome === 'added') {
      addToast({ title: 'Passkey created', data: { status: 'success' } });
    }
    if (outcome === 'added' || outcome === 'already-on-device') {
      markPasskeyOnThisBrowser(userId);
      setOpen(false);
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) dismiss();
  };

  const error = passkeys.state.status === 'error' && (
    <OfferError role="alert">
      <Typography variant="body3" color="inherit">
        {passkeys.state.message}
      </Typography>
    </OfferError>
  );
  const createLabel = passkeys.isAdding ? 'Creating passkey…' : 'Create passkey';

  return (
    <>
      {isMobile ? (
        <BottomSheet
          open={open}
          onOpenChange={handleOpenChange}
          title={TITLE}
          headerIcon={<PasskeyIcon />}
          showClose={false}
          primaryAction={{
            children: (
              <>
                <PasskeyIcon aria-hidden="true" />
                {createLabel}
              </>
            ),
            disabled: passkeys.isAdding,
            onClick: () => void create(),
          }}
          secondaryAction={{ children: 'Not now', variant: 'ghost', onClick: dismiss }}
        >
          <OfferDescription variant="body2">{DESCRIPTION}</OfferDescription>
          {error}
        </BottomSheet>
      ) : (
        <Modal
          open={open}
          onOpenChange={handleOpenChange}
          variant="center"
          title={TITLE}
          titleVariant="h5"
          description={DESCRIPTION}
          headerIcon={<PasskeyIcon />}
          showClose={false}
          maxWidth={480}
          actionsLayout="stack"
          primaryAction={{
            label: createLabel,
            icon: <PasskeyIcon aria-hidden="true" />,
            disabled: passkeys.isAdding,
            onClick: () => void create(),
          }}
          secondaryAction={{ label: 'Not now', variant: 'ghost', onClick: dismiss }}
        >
          {error || null}
        </Modal>
      )}
      <MfaVerificationDialog controller={verification} />
    </>
  );
};
